// frontend/src/farm/lawnProps.mjs
// The scenery on the grass OUTSIDE the fence: flowers, plants, stones, bushes and groves of
// trees (owner, 2026-09-30). Pure — no Phaser — so `node --test` covers it.
//
// Each player's scenery comes from a seed (seedFrom(player id)): the same for them and for
// anyone visiting, different on another player's farm. It is laid out ONCE on a fixed patch of
// land measured from the farm's home (left) corner — the corner that stays put while the farm
// grows to the right — and every farm size only FILTERS that layout. The filter gets stricter as
// the fence comes closer, so growing only takes props away near the fence: nothing moves.
// Density rises with the distance from the fence: sparse low flowers by it, bushes further out,
// groves of tall trees at the edge of the lawn.
//
// Coordinates out are the FarmWorld container's LOCAL px, with (0.5, 1) origins: each {x, y} is
// where the prop's base touches the ground.
import { HORIZON_MARGIN, HORIZON_CLEAR, TILE_H, PATH_LEN as PATH_LEN_CELLS, cellFToLocal, cellsOutside, pathCells } from './iso.mjs';
import { mulberry32 } from './rng.mjs';
import { inPlotClear } from './podworkoPlot.mjs';

// Tall trees keep 4 cells off the fence (they'd cover plots), young trees and shrubs 2, low ones 1.
const TALL_KINDS = ['tree', 'brzoza', 'sosna', 'swierk', 'dab', 'lipa'];
const MID_KINDS = ['swierk_young', 'brzoza_young', 'leszczyna', 'jalowiec', 'bush', 'dzika_roza', 'trzcina'];
const LOW_KINDS = ['flowers_pink', 'flowers_yellow', 'maki', 'chabry', 'rumianek', 'mlecze', 'tuft', 'paproc',
  'rock', 'kamien_mech', 'pniak', 'kloda', 'muchomory'];
export const TALL = new Set(TALL_KINDS);
export const PROP_KINDS = [...TALL_KINDS, ...MID_KINDS, ...LOW_KINDS];
export const MIN_BAND = Object.fromEntries(PROP_KINDS.map((k) => [k, TALL.has(k) ? 4 : MID_KINDS.includes(k) ? 2 : 1]));

// Display height of each prop (scripts/farm_scenery.py PROP_BOX, halved: the art is 2×).
export const PROP_HEIGHT = {
  tree: 140, brzoza: 150, sosna: 165, swierk: 150, dab: 150, lipa: 150,
  swierk_young: 70, brzoza_young: 80, leszczyna: 70, jalowiec: 70, bush: 65, dzika_roza: 55, trzcina: 60,
  flowers_pink: 45, flowers_yellow: 45, maki: 40, chabry: 40, rumianek: 40, mlecze: 38, tuft: 45, paproc: 45,
  rock: 35, kamien_mech: 45, pniak: 35, kloda: 35, muchomory: 30,
};

// How common each scattered (non-grove) prop is.
const SCATTER_WEIGHTS = {
  flowers_pink: 4, flowers_yellow: 4, maki: 4, chabry: 4, rumianek: 4, mlecze: 4, tuft: 7, paproc: 4,
  rock: 2, kamien_mech: 2, pniak: 2, kloda: 1.5, muchomory: 1.5,
  bush: 3, dzika_roza: 2, trzcina: 2, jalowiec: 1.5, leszczyna: 1.5, swierk_young: 1, brzoza_young: 1,
  tree: 0.2, brzoza: 0.15, lipa: 0.15, dab: 0.15,          // the odd lone tree; most stand in groves
};
// Groves: a leading species (mostly), the odd other tree, some undergrowth round the edge.
const GROVES = [
  { lead: 'swierk', mix: ['sosna', 'swierk_young'], under: ['jalowiec', 'paproc', 'muchomory'] },
  { lead: 'sosna', mix: ['swierk', 'brzoza'], under: ['jalowiec', 'paproc', 'kamien_mech'] },
  { lead: 'brzoza', mix: ['brzoza_young', 'sosna'], under: ['paproc', 'muchomory', 'tuft'] },
  { lead: 'dab', mix: ['lipa', 'tree'], under: ['leszczyna', 'pniak', 'paproc'] },
  { lead: 'tree', mix: ['lipa', 'dab'], under: ['leszczyna', 'bush', 'kloda'] },
];

// FOREST meadow GARDEN meadow FOREST (owner, 2026-09-30), in SCREEN columns: dense forest more
// than MEADOW_PX to the left / right of the farm, from the hedge down to the bottom of the view
// (the lower corners too); meadow between, and above / below the farm — no forest under it (a
// belt that followed the diamond ran down in a V and left the lower corners bare).
export const MEADOW_PX = 7 * 96;
// How far past the farm the camera can ever show: the view is clamped with the farm's edge at a
// quarter of the viewport (farmView.clampView), at the minimum zoom 0.5 — 0.75 × 1280 / 0.5 across,
// 0.75 × 720 / 0.5 down.
export const VIEW_REACH = { x: 1920, y: 1080 };
// …and across the bottom: the lowest third of the space under the farm the camera can show (owner,
// 2026-09-30: "1/3 of the empty space from the bottom should also be forest").
export const BOTTOM_FOREST_PX = Math.round(VIEW_REACH.y * 0.6);   // a bit over 1/3: closer in, the view stops short of the reach
const EDGE_PX = 160;                 // the forest's edge wanders this much: no ruler-straight wall
const FOREST_P = 0.9;                // a forest tree candidate on this share of the cells
const UNDER_P = 0.3;                 // and some undergrowth

const MAX_SIZE = 24;                 // the biggest farm (farm_catalog EXPANSIONS)
const HW = 48, HH = 24;              // iso half tile (iso.mjs)
const SCATTER_P = 0.5;               // a candidate on this share of the cells
const GROVE_P = 0.018;               // a grove centre on this share
const SPACING = (k) => (TALL.has(k) ? 0.95 : MID_KINDS.includes(k) ? 0.7 : 0.45);
const CLEAR = 1;                     // cells kept free round the path and the two corner exits
// Nothing above this local y: the far hedge overlaps the lawn's top edge (iso.HORIZON_CLEAR),
// and a prop's TOP stays below the near band's lowest edge (48 local px at the minimum zoom 0.5).
const TOP_LIMIT = -HORIZON_MARGIN * TILE_H + HORIZON_CLEAR;
const HEDGE_BOTTOM = -HORIZON_MARGIN * TILE_H + 48;

// A player id (or any string) → a 32-bit seed (FNV-1a).
export function seedFrom(str) {
  let h = 0x811c9dc5;
  for (const ch of String(str)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}

const clamp01 = (v) => Math.max(0, Math.min(1, v));
// The chance a prop stays at `d` cells from the fence — rising with d, so a grow (d falls) only removes.
function keepChance(kind, d) {
  if (TALL.has(kind)) return clamp01((d - 3.5) / 3);
  if (MID_KINDS.includes(kind)) return 0.25 + 0.75 * clamp01((d - 2) / 4);
  return 0.25 + 0.65 * clamp01((d - 1) / 5);
}

function pick(rnd, weights) {
  const entries = Object.entries(weights);
  let r = rnd() * entries.reduce((s, [, w]) => s + w, 0);
  for (const [k, w] of entries) if ((r -= w) <= 0) return k;
  return entries[entries.length - 1][0];
}

const _worlds = new Map();
// Every prop the seed can ever show, in cells relative to the home corner (rx = x, ry = y − size),
// already spaced out — independent of the farm size.
function world(seed) {
  if (_worlds.has(seed)) return _worlds.get(seed);
  const rnd = mulberry32(seed * 2654435761 + 12345);
  const raw = [];
  const add = (kind, rx, ry, u = rnd(), forest = false) => raw.push({ kind, rx, ry, u, forest, edge: rnd() * EDGE_PX, flip: rnd() < 0.5, scale: 0.9 + 0.2 * rnd() });
  // Everything any farm size can show, relative to the home corner: local X = (rx − ry)·HW,
  // Y = (rx + ry)·HH. Walk the cells of that box and skip the ones outside it.
  const X0 = -VIEW_REACH.x - 150, X1 = MAX_SIZE * 2 * HW + VIEW_REACH.x + 150;
  const Y0 = -MAX_SIZE * HH - HORIZON_MARGIN * TILE_H - 50, Y1 = MAX_SIZE * HH + VIEW_REACH.y + 150;
  const inBox = (rx, ry) => { const X = (rx - ry) * HW, Y = (rx + ry) * HH; return X >= X0 && X <= X1 && Y >= Y0 && Y <= Y1; };
  const x0 = Math.floor((X0 / HW + Y0 / HH) / 2), x1 = Math.ceil((X1 / HW + Y1 / HH) / 2);
  const y0 = Math.floor((Y0 / HH - X1 / HW) / 2), y1 = Math.ceil((Y1 / HH - X0 / HW) / 2);
  let cells = 0;
  for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) if (inBox(x + 0.5, y + 0.5)) cells++;
  for (let g = 0, n = Math.round(cells * GROVE_P); g < n; g++) {             // groves first: they win the space
    let gx, gy;
    do { gx = x0 + rnd() * (x1 - x0); gy = y0 + rnd() * (y1 - y0); } while (!inBox(gx, gy));
    const grove = GROVES[Math.floor(rnd() * GROVES.length)];
    const trees = 3 + Math.floor(rnd() * 6);
    const gu = rnd() * 0.9;                                  // one roll per grove: it thins as a group, no loners
    for (let t = 0; t < trees; t++) {
      const a = rnd() * Math.PI * 2, r = Math.sqrt(rnd()) * 2.2;
      add(rnd() < 0.7 ? grove.lead : grove.mix[Math.floor(rnd() * grove.mix.length)], gx + Math.cos(a) * r, gy + Math.sin(a) * r,
        gu + rnd() * 0.1);
    }
    for (let t = 0, under = 1 + Math.floor(rnd() * 3); t < under; t++) {
      const a = rnd() * Math.PI * 2, r = 2 + rnd() * 1.5;
      add(grove.under[Math.floor(rnd() * grove.under.length)], gx + Math.cos(a) * r, gy + Math.sin(a) * r);
    }
  }
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      if (!inBox(x + 0.5, y + 0.5) || rnd() >= SCATTER_P) continue;
      add(pick(rnd, SCATTER_WEIGHTS), x + rnd(), y + rnd());
    }
  }
  // The forest belt: its own layer (spaced only against itself, so it never punches holes in the
  // meadow of a smaller farm), species in patches of ~5 cells: a birch stand, a spruce stand…
  const under = [];
  const patchRnd = (px, py) => mulberry32(seed ^ Math.imul(px + 1000, 73856093) ^ Math.imul(py + 1000, 19349663))();
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      if (!inBox(x + 0.5, y + 0.5)) continue;
      const jx = x + rnd(), jy = y + rnd();
      const grove = GROVES[Math.floor(patchRnd(Math.floor((jx + 40) / 5), Math.floor((jy + 40) / 5)) * GROVES.length)];
      if (rnd() < FOREST_P) {
        const r = rnd();
        const kind = r < 0.6 ? grove.lead : r < 0.9 ? grove.mix[Math.floor(rnd() * grove.mix.length)] : TALL_KINDS[Math.floor(rnd() * TALL_KINDS.length)];
        add(kind, jx, jy, rnd(), true);
      }
      if (rnd() < UNDER_P) under.push([grove, x + rnd(), y + rnd()]);
    }
  }
  for (const [grove, ux, uy] of under) add(grove.under[Math.floor(rnd() * grove.under.length)], ux, uy, rnd(), true);   // after the trees: gaps only
  // Space them out (first come first served, so groves keep their trees), on a coarse grid per layer.
  const kept = [], grids = [new Map(), new Map()];
  const cellKey = (x, y) => `${Math.floor(x)},${Math.floor(y)}`;
  for (const p of raw) {
    const grid = grids[p.forest ? 1 : 0];
    const r = SPACING(p.kind);
    let clash = false;
    for (let dx = -1; dx <= 1 && !clash; dx++) {
      for (let dy = -1; dy <= 1 && !clash; dy++) {
        for (const o of grid.get(cellKey(p.rx + dx, p.ry + dy)) ?? []) {
          if (Math.hypot(o.rx - p.rx, o.ry - p.ry) < Math.max(r, SPACING(o.kind))) { clash = true; break; }
        }
      }
    }
    if (clash) continue;
    kept.push(p);
    const k = cellKey(p.rx, p.ry);
    grid.set(k, [...(grid.get(k) ?? []), p]);
  }
  _worlds.set(seed, kept);
  return kept;
}

// Cell-space points that must stay clear: the path tiles' centres and the two corner exits
// (the left corner is the way home; the right one stays clear too — it led to the Zagroda).
function keepClear(size) {
  return [
    ...pathCells(size).map((c) => ({ x: c.x + 0.5, y: c.y + 0.5 })),
    { x: 0, y: size },
    { x: size, y: 0 },
  ];
}

// The player's house (owner, 2026-09-30) beside the farm, as on the main screen (NOT mirrored: its
// front door faces away, lower left) — the garden is entered from the back of the house, so the
// dirt path runs in behind it: the path's last tile sits under the house's back half. Art:
// farm_house_lvl1..5 (scripts/farm_house.py). PATH_END = where that tile's centre falls, as
// fractions of the sprite (all levels share one canvas); H follows the art's aspect.
export const HOUSE = { w: 420, h: 444, pathEnd: [0.72, 0.6] };
export function housePlacement(size, level = 1, house = HOUSE) {
  const end = cellFToLocal(0.5, size + PATH_LEN_CELLS - 0.5, size);
  const [fx, fy] = house.pathEnd;
  const x0 = end.x - fx * house.w, y1 = end.y + (1 - fy) * house.h;
  const rect = { x0, x1: x0 + house.w, y0: y1 - house.h, y1 };
  // Nothing may stand over it or in front of it: a prop's base inside this box (half a wide tree's
  // width either side; below the base until the tallest tree's crown can't reach it) is left out.
  // (from the sprite's top: a tree just behind it would stick out over the roof)
  const clear = { x0: rect.x0 - 70, x1: rect.x1 + 70, y0: rect.y0, y1: rect.y1 + 170 };
  return { rect, clear, level };
}

export function lawnProps(size, seed = 1, { podworko = false } = {}) {
  const clear = keepClear(size);
  const boxes = [1, 2, 3, 4, 5].map((lv) => housePlacement(size, lv).clear);   // clear for every level's spot
  const hc = { x0: Math.min(...boxes.map((b) => b.x0)), x1: Math.max(...boxes.map((b) => b.x1)),
    y0: Math.min(...boxes.map((b) => b.y0)), y1: Math.max(...boxes.map((b) => b.y1)) };
  const W = size * 2 * HW, h = size * 2 * HH;
  const out = [];
  for (const p of world(seed >>> 0)) {
    const cx = p.rx, cy = p.ry + size;
    const d = cellsOutside(cx, cy, size);
    if (d <= 0) continue;
    const l = cellFToLocal(cx, cy, size);
    if (l.x < -VIEW_REACH.x - 100 || l.x > W + VIEW_REACH.x + 100 || l.y > h + VIEW_REACH.y + 100) continue;
    const side = l.x < 0 ? -l.x : l.x > W ? l.x - W : 0;           // px to the left / right of the farm
    const woods = side >= MEADOW_PX + p.edge || l.y - h >= BOTTOM_FOREST_PX + p.edge;
    if (woods !== p.forest) continue;                                // forest round the sides and bottom, meadow between
    if (!p.forest) {
      // straight above / below the farm the meadow thins out again past ~6 cells (owner: it ran too low)
      const fade = side === 0 ? 1 - 0.85 * Math.max(0, Math.min(1, (d - 5) / 4)) : 1;
      if (d < MIN_BAND[p.kind] || p.u >= keepChance(p.kind, d) * fade) continue;
    } else if (TALL.has(p.kind) && d < 4) continue;
    if (clear.some((c) => Math.max(Math.abs(cx - c.x), Math.abs(cy - c.y)) <= CLEAR)) continue;
    if (podworko && inPlotClear(cx, cy - size, size)) continue;            // the house + Podwórko plot (spec 2026-10-22)
    if (l.y < TOP_LIMIT || l.y - PROP_HEIGHT[p.kind] * p.scale < HEDGE_BOTTOM) continue;
    if (l.x > hc.x0 && l.x < hc.x1 && l.y > hc.y0 && l.y < hc.y1) continue;      // keep the house in view
    out.push({ kind: p.kind, x: l.x, y: l.y, flip: p.flip, scale: p.scale, forest: p.forest });
  }
  return out;
}
